import { Component, OnInit } from '@angular/core';
import { EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { ResetPasswordService } from '../../../../core/services/reset-password.service';
import { UiHelperService } from '../../../../services/ui-helper.service';
import { NotificationService } from '../../../../services/notification.service';
import Swal from 'sweetalert2';
import {
  tokenVirtualPasswordValidators,
  passwordHasMinLength,
  passwordHasUpper,
  passwordHasNumber,
  passwordHasSpecial,
  PASSWORD_MAX_LENGTH
} from '../../../../core/validators/password.validators';

@Component({
  selector: 'app-reset-step-cambio-contrasena',
  templateUrl: './step-cambio-contrasena.component.html',
  styleUrls: ['./step-cambio-contrasena.component.css'],
  standalone: false
})
export class StepCambioContrasenaComponent implements OnInit {
  @Output() next = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  form!: FormGroup;
  datosVerificacion: any = null;
  showNueva = false;
  showConfirmar = false;
  readonly passwordMaxLength = PASSWORD_MAX_LENGTH;

  constructor(
    private fb: FormBuilder,
    private resetPassword: ResetPasswordService,
    private uiHelper: UiHelperService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      nueva: ['', tokenVirtualPasswordValidators()],
      confirmar: ['', Validators.required]
    });
    const raw = this.resetPassword.getStorage('datosVerificacion');
    if (raw) {
      try { this.datosVerificacion = JSON.parse(raw); } catch (_) {}
    }
  }

  hasError(campo: string): boolean {
    const c = this.form.get(campo);
    return !!(c && c.invalid && (c.touched || c.dirty));
  }

  getErrorMessage(campo: string): string {
    const c = this.form.get(campo);
    if (!c || !c.errors) return '';
    if (c.errors['required']) return 'Campo obligatorio';
    if (c.errors['minlength']) return 'Mínimo 8 caracteres';
    if (c.errors['maxlength']) return 'Máximo 12 caracteres';
    if (c.errors['pattern']) return 'Debe incluir mayúscula, número y carácter especial';
    return '';
  }

  get passwordNueva(): string {
    return this.form.get('nueva')?.value || '';
  }

  passwordsMatch(): boolean {
    const n = this.form.get('nueva')?.value;
    const c = this.form.get('confirmar')?.value;
    return !!n && n === c;
  }

  get hasMinLength(): boolean { return passwordHasMinLength(this.passwordNueva); }
  get hasUpper(): boolean { return passwordHasUpper(this.passwordNueva); }
  get hasNumber(): boolean { return passwordHasNumber(this.passwordNueva); }
  get hasSpecial(): boolean { return passwordHasSpecial(this.passwordNueva); }

  ngSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (!this.passwordsMatch()) {
      Swal.fire({ title: 'Aviso', text: 'Las contraseñas no coinciden.', icon: 'warning', ...this.notification.getSwalBaseConfig() });
      return;
    }
    this.cambiarContrasena();
  }

  cambiarContrasena(): void {
    if (!this.datosVerificacion) {
      Swal.fire({ title: 'Error', text: 'Faltan datos. Vuelve al paso anterior.', icon: 'error', ...this.notification.getSwalBaseConfig() });
      return;
    }
    const ordenPedidoId = this.resetPassword.getStorage('ordenPedidoId');
    const solicitudTitularId = this.resetPassword.getStorage('solicitudTitularId');
    if (!ordenPedidoId || !solicitudTitularId) {
      Swal.fire({ title: 'Error', text: 'Falta información de orden. Vuelve al paso anterior.', icon: 'error', ...this.notification.getSwalBaseConfig() });
      return;
    }
    const request = {
      tipoIdentificacionId: parseInt(this.datosVerificacion.tipoIdentificacion, 10),
      numeroIdentificacion: this.datosVerificacion.numeroIdentificacion,
      passwordNuevo: this.form.value.nueva,
      ordenPedidoId: parseInt(ordenPedidoId, 10),
      solicitudTitularId: parseInt(solicitudTitularId, 10)
    };
    this.uiHelper.showSpinner('Cambiando contraseña...', 'admin');
    const baseConfig = this.notification.getSwalBaseConfig();
    this.resetPassword.cambiarContrasena(request).pipe(
      finalize(() => this.uiHelper.hideSpinner())
    ).subscribe({
      next: (res) => {
        const data = res?.data || (res as any);
        if (data?.exitoso === true) {
          this.resetPassword.clearFlowStorage();
          this.next.emit();
        } else {
          Swal.fire({ title: 'Error', text: data?.mensaje || 'No se pudo cambiar la contraseña.', icon: 'error', ...baseConfig });
        }
      },
      error: (err) => {
        const msg = (err && (err as any).message) || err?.error?.mensaje || err?.error?.message || 'No se pudo cambiar la contraseña.';
        Swal.fire({
          title: 'Error',
          text: msg,
          icon: 'error',
          ...this.notification.getSwalBaseConfigReloadAfterClose()
        });
      }
    });
  }
}
